// ── Seed pool constants (Round 13 Phase D) ──
//
// Per-realm seed pools with growth time ranges and material output.
// Sources: plan-13 §4, loot-and-materials.md §4.1–§4.3.
import { ItemType } from '../types/loot.js';
import { RealmId } from './realms.js';
// Time constants
const HOURS = 60 * 60 * 1000;
const DAYS = 24 * HOURS;
/** All seed definitions, keyed by seedId. */
export const SEED_DEFS = {
    // ── Common seeds (1–3 days) — available in all realms ──
    wildflower: {
        seedId: 'wildflower',
        displayName: 'Wildflower Seed',
        tier: 'common',
        growthMinMs: 1 * DAYS,
        growthMaxMs: 3 * DAYS,
        harvestItem: ItemType.MaterialWood,
        harvestQuantity: { min: 2, max: 5 },
        realmAffinity: [RealmId.VerdanaFields, RealmId.SablewindReaches, RealmId.FrostmereExpanse, RealmId.EmberMire],
    },
    meadow_grass: {
        seedId: 'meadow_grass',
        displayName: 'Meadow Grass Seed',
        tier: 'common',
        growthMinMs: 1 * DAYS,
        growthMaxMs: 2 * DAYS,
        harvestItem: ItemType.MaterialThatch,
        harvestQuantity: { min: 3, max: 6 },
        realmAffinity: [RealmId.VerdanaFields, RealmId.SablewindReaches, RealmId.FrostmereExpanse, RealmId.EmberMire],
    },
    herb_patch: {
        seedId: 'herb_patch',
        displayName: 'Herb Patch Seed',
        tier: 'common',
        growthMinMs: 1 * DAYS,
        growthMaxMs: 3 * DAYS,
        harvestItem: ItemType.MaterialCloth,
        harvestQuantity: { min: 1, max: 3 },
        realmAffinity: [RealmId.VerdanaFields, RealmId.SablewindReaches, RealmId.FrostmereExpanse, RealmId.EmberMire],
    },
    // ── Realm-specific seeds (3–14 days) ──
    verdana_moss: {
        seedId: 'verdana_moss',
        displayName: 'Verdana Moss Spore',
        tier: 'realm_specific',
        growthMinMs: 3 * DAYS,
        growthMaxMs: 14 * DAYS,
        harvestItem: ItemType.MaterialWood,
        harvestQuantity: { min: 5, max: 12 },
        realmAffinity: [RealmId.VerdanaFields],
    },
    sablewind_cactus: {
        seedId: 'sablewind_cactus',
        displayName: 'Sablewind Cactus Seed',
        tier: 'realm_specific',
        growthMinMs: 3 * DAYS,
        growthMaxMs: 14 * DAYS,
        harvestItem: ItemType.MaterialInk,
        harvestQuantity: { min: 3, max: 8 },
        realmAffinity: [RealmId.SablewindReaches],
    },
    frostmere_lichen: {
        seedId: 'frostmere_lichen',
        displayName: 'Frostmere Lichen Spore',
        tier: 'realm_specific',
        growthMinMs: 3 * DAYS,
        growthMaxMs: 14 * DAYS,
        harvestItem: ItemType.MaterialThawedWater,
        harvestQuantity: { min: 3, max: 8 },
        realmAffinity: [RealmId.FrostmereExpanse],
    },
    ember_bloom: {
        seedId: 'ember_bloom',
        displayName: 'Ember Bloom Seed',
        tier: 'realm_specific',
        growthMinMs: 3 * DAYS,
        growthMaxMs: 14 * DAYS,
        harvestItem: ItemType.MaterialEmber,
        harvestQuantity: { min: 3, max: 8 },
        realmAffinity: [RealmId.EmberMire],
    },
    // ── Rare seeds (14–30 days) ──
    ancient_oak: {
        seedId: 'ancient_oak',
        displayName: 'Ancient Oak Acorn',
        tier: 'rare',
        growthMinMs: 14 * DAYS,
        growthMaxMs: 30 * DAYS,
        harvestItem: ItemType.MaterialWood,
        harvestQuantity: { min: 10, max: 25 },
        realmAffinity: [RealmId.VerdanaFields, RealmId.SablewindReaches, RealmId.FrostmereExpanse, RealmId.EmberMire],
    },
    wax_lily: {
        seedId: 'wax_lily',
        displayName: 'Wax Lily Bulb',
        tier: 'rare',
        growthMinMs: 14 * DAYS,
        growthMaxMs: 30 * DAYS,
        harvestItem: ItemType.MaterialWax,
        harvestQuantity: { min: 5, max: 15 },
        realmAffinity: [RealmId.VerdanaFields, RealmId.SablewindReaches, RealmId.FrostmereExpanse, RealmId.EmberMire],
    },
    ash_rose: {
        seedId: 'ash_rose',
        displayName: 'Ash Rose Seed',
        tier: 'rare',
        growthMinMs: 14 * DAYS,
        growthMaxMs: 30 * DAYS,
        harvestItem: ItemType.MaterialAsh,
        harvestQuantity: { min: 5, max: 15 },
        realmAffinity: [RealmId.EmberMire, RealmId.SablewindReaches],
    },
};
/** Seed drop chance from gathering (5% per plan-13 §4). */
export const SEED_DROP_CHANCE = 0.05;
/** Minimum growth time floor (24 hours per plan-13 design note). */
export const SEED_MIN_GROWTH_MS = 24 * HOURS;
/** Maximum seeds a character can have planted at once. */
export const SEED_MAX_PLANTED = 12;
/**
 * Get the seed pool for a given realm. Returns all seeds whose
 * realmAffinity includes the specified realm.
 */
export function getSeedPoolForRealm(realmId) {
    return Object.values(SEED_DEFS).filter(s => s.realmAffinity.includes(realmId));
}
/**
 * Pick a random seed from a realm's pool, weighted by tier:
 * - common: 70%
 * - realm_specific: 25%
 * - rare: 5%
 */
export function rollSeedForRealm(realmId) {
    const pool = getSeedPoolForRealm(realmId);
    if (pool.length === 0)
        return null;
    const tierRoll = Math.random();
    let targetTier;
    if (tierRoll < 0.70)
        targetTier = 'common';
    else if (tierRoll < 0.95)
        targetTier = 'realm_specific';
    else
        targetTier = 'rare';
    const candidates = pool.filter(s => s.tier === targetTier);
    if (candidates.length === 0) {
        // Fallback to common if the target tier has no entries for this realm
        const fallback = pool.filter(s => s.tier === 'common');
        if (fallback.length === 0)
            return pool[0];
        return fallback[Math.floor(Math.random() * fallback.length)];
    }
    return candidates[Math.floor(Math.random() * candidates.length)];
}
//# sourceMappingURL=seeds.js.map